import { useEffect, useState, type ReactNode } from 'react';
import type { Lang } from '../data/types';
import { useDashboard, useDashboardActions } from '../data/context';
import { t } from '../i18n/t';
import { NumStep } from '../components/NumStep';
import { Switch } from '../components/Switch';
import { STYLE_DEFAULTS, type BrandColors } from '../data/styleDefaults';

type Tab = 'general' | 'rules' | 'branding';

interface SettingsViewProps {
  lang: Lang;
}

export function SettingsView({ lang }: SettingsViewProps) {
  const { serviceCfg, tenant, settings } = useDashboard();
  const actions = useDashboardActions();
  const [tab, setTab] = useState<Tab>('general');

  // Each toggle writes through to the API immediately; the dashboard
  // context is the source of truth for current values.
  const toggleGuest = () =>
    void actions.updateSettings({ guestBooking: !settings.guestBooking });
  const toggleAuto = () =>
    void actions.updateSettings({ autoConfirm: !settings.autoConfirm });
  const toggleEmail = () =>
    void actions.updateSettings({ emailNotif: !settings.emailNotif });
  const setAutoThreshold = (n: number) =>
    void actions.updateSettings({ autoConfirmThreshold: n });

  const tabs: { id: Tab; label: string }[] = [
    { id: 'general', label: t('general', lang) },
    { id: 'rules', label: t('bookingRules', lang) },
    { id: 'branding', label: t('branding', lang) },
  ];

  // The autoConfirmSub copy contains a `{n}` placeholder where we embed the
  // inline NumStep — split the string around it.
  const autoSub = (): ReactNode => {
    const [before, after] = t('autoConfirmSub', lang).split('{n}');
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <span>{before}</span>
        <NumStep
          value={settings.autoConfirmThreshold}
          min={1}
          max={20}
          onChange={setAutoThreshold}
          disabled={!settings.autoConfirm}
        />
        <span>{after}</span>
      </span>
    );
  };

  const rules: {
    k: 'guest' | 'auto' | 'email';
    on: boolean;
    onToggle: () => void;
    nameKey: string;
    subKey: string;
  }[] = [
    { k: 'guest', on: settings.guestBooking, onToggle: toggleGuest, nameKey: 'guestBooking', subKey: 'guestBookingSub' },
    { k: 'auto', on: settings.autoConfirm, onToggle: toggleAuto, nameKey: 'autoConfirm', subKey: 'autoConfirmSub' },
    { k: 'email', on: settings.emailNotif, onToggle: toggleEmail, nameKey: 'emailNotif', subKey: 'emailNotifSub' },
  ];

  return (
    <div className="view">
      <div className="view-pad">
        <div className="setwrap">
          <nav className="set-side" aria-label="Settings sections">
            {tabs.map((x) => (
              <a
                key={x.id}
                className={tab === x.id ? 'on' : ''}
                href={`#${x.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  setTab(x.id);
                }}
              >
                {x.label}
              </a>
            ))}
          </nav>

          <div>
            {tab === 'general' && (
              <div className="set-block">
                <h2>{t('general', lang)}</h2>
                <div className="sub">{tenant.name}</div>
                <div className="field">
                  <label htmlFor="biz-name">{t('bizName', lang)}</label>
                  <input id="biz-name" className="input" defaultValue={tenant.name} />
                </div>
                <div className="field-row">
                  <div className="field">
                    <label htmlFor="biz-phone">{t('phone', lang)}</label>
                    <input
                      id="biz-phone"
                      className="input tnum"
                      placeholder="+39 …"
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="biz-email">{t('email', lang)}</label>
                    <input
                      id="biz-email"
                      className="input"
                      placeholder="prenotazioni@iltuolocale.it"
                    />
                  </div>
                </div>
                <div className="field">
                  <label htmlFor="turn-time">{t('turnTime', lang)}</label>
                  <input
                    id="turn-time"
                    className="input tnum"
                    defaultValue={`${serviceCfg.turn} ${t('minutes', lang)}`}
                    style={{ maxWidth: 180 }}
                  />
                </div>
                <button type="button" className="btn btn-primary" style={{ marginTop: 8 }}>
                  {t('save', lang)}
                </button>
              </div>
            )}

            {tab === 'rules' && (
              <div className="set-block">
                <h2>{t('bookingRules', lang)}</h2>
                <div className="sub">{tenant.name} · portale prenotazioni</div>

                {/* Capacity ceiling — the safety net for auto-confirm. Even
                    bookings that match the threshold get refused (and fall
                    to manual review) if accepting them would push total
                    concurrent covers over this number. */}
                <div className="field" style={{ marginTop: 20, marginBottom: 26 }}>
                  <label htmlFor="max-covers">
                    {lang === 'it' ? 'Capienza massima del locale' : 'Venue max capacity'}
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <input
                      id="max-covers"
                      type="number"
                      min={1}
                      max={500}
                      className="input tnum"
                      value={settings.maxConcurrentCovers}
                      onChange={(e) => {
                        const n = Math.max(1, Math.min(500, Number(e.target.value) || 1));
                        void actions.updateSettings({ maxConcurrentCovers: n });
                      }}
                      style={{ maxWidth: 110 }}
                    />
                    <span className="tr-sub" style={{ flex: 1 }}>
                      {lang === 'it'
                        ? 'Coperti massimi simultanei. La conferma automatica non supera mai questo numero.'
                        : 'Max concurrent covers. Auto-confirm will never exceed it.'}
                    </span>
                  </div>
                </div>

                {rules.map((row) => (
                  <div className="toggle-row" key={row.k}>
                    <div className="tr-body">
                      <div className="tr-name">{t(row.nameKey, lang)}</div>
                      <div className="tr-sub">
                        {row.k === 'auto' ? autoSub() : t(row.subKey, lang)}
                      </div>
                    </div>
                    <Switch
                      on={row.on}
                      onToggle={row.onToggle}
                      ariaLabel={t(row.nameKey, lang)}
                    />
                  </div>
                ))}
              </div>
            )}

            {tab === 'branding' && <BrandingTab lang={lang} />}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Brand: editor reale dei 4 colori del sito ──────────────────────────────
// Salva nel content del sito (theme) e pubblica: il sito si ricostruisce coi
// colori nuovi e l'accento ridipinge anche il portale (tenants.accent sync).
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

function BrandingTab({ lang }: { lang: Lang }) {
  const { tenant } = useDashboard();
  const actions = useDashboardActions();

  // Default = i colori VERI dello Stile del cliente (non più i fissi "classico").
  // L'accento personalizzato dal titolare (tenant.brand) ha la precedenza.
  const sd = STYLE_DEFAULTS[tenant.designStyle ?? 'classico'] ?? {
    primary: '#2F3D2E', background: '#FBF7EF', text: '#26241D', accent: '#B0833C',
  };
  const defaults: BrandColors = {
    primary: sd.primary,
    background: sd.background,
    text: sd.text,
    accent: tenant.brand?.accent ?? sd.accent,
  };

  const [colors, setColors] = useState<BrandColors>(defaults);
  const [status, setStatus] = useState<'loading' | 'idle' | 'saving' | 'done' | 'error'>('loading');

  useEffect(() => {
    let alive = true;
    actions
      .loadSite()
      .then((site) => {
        if (!alive) return;
        // Il brand vive nel draft (più aggiornato); ripiego sul published.
        const th = site.draft?.theme ?? site.published?.theme ?? {};
        setColors({
          primary: th.primary ?? defaults.primary,
          background: th.background ?? defaults.background,
          text: th.text ?? defaults.text,
          accent: th.accent ?? defaults.accent,
        });
        setStatus('idle');
      })
      .catch(() => {
        if (alive) setStatus('error');
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    if (status === 'saving' || status === 'loading') return;
    setStatus('saving');
    try {
      // Salvataggio brand DEDICATO: indipendente dal resto del sito, così una
      // riga di menù incompleta non può più bloccare il cambio colori.
      await actions.saveTheme({ ...colors });
      setStatus('done');
    } catch (err) {
      console.error('[branding] save failed:', err);
      setStatus('error');
    }
  };

  // Ordine logico (lo sfondo, il testo sopra, poi i due colori che spiccano) e
  // nomi che dicono COSA fa ciascuno — non "Brand/Accento" che confondono.
  const FIELDS: { key: keyof BrandColors; it: string; en: string; descIt: string; descEn: string }[] = [
    {
      key: 'background',
      it: 'Sfondo',
      en: 'Background',
      descIt: 'Il fondo di tutta la pagina.',
      descEn: 'The page background.',
    },
    {
      key: 'text',
      it: 'Testo',
      en: 'Text',
      descIt: 'Il testo che si legge. Dev’essere ben leggibile sullo sfondo.',
      descEn: 'The body text. Keep it readable on the background.',
    },
    {
      key: 'primary',
      it: 'Bottoni e titoli',
      en: 'Buttons & headings',
      descIt: 'Il colore forte del locale: i bottoni (es. «Prenota») e i titoli importanti.',
      descEn: 'The venue’s strong colour: buttons (e.g. “Book”) and key headings.',
    },
    {
      key: 'accent',
      it: 'Accento',
      en: 'Accent',
      descIt: 'I dettagli che spiccano: link, stelle, parole in evidenza.',
      descEn: 'Standout details: links, stars, highlighted words.',
    },
  ];

  const it = lang === 'it';
  // testo leggibile sopra il colore "Bottoni" (chiaro su scuro, scuro su chiaro)
  const onPrimary = (() => {
    const h = colors.primary.replace('#', '');
    if (h.length < 6) return '#fff';
    const l =
      (0.299 * parseInt(h.slice(0, 2), 16) +
        0.587 * parseInt(h.slice(2, 4), 16) +
        0.114 * parseInt(h.slice(4, 6), 16)) /
      255;
    return l < 0.6 ? '#fff' : '#1a1a1a';
  })();

  return (
    <div className="set-block">
      <h2>{t('branding', lang)}</h2>
      <div className="sub">
        {it
          ? 'I colori del vostro sito. Si salvano subito e indipendentemente dal resto: il sito si aggiorna in 2-3 minuti.'
          : 'Your website colours. They save on their own, regardless of the rest of the site: it updates within 2-3 minutes.'}
      </div>

      {/* anteprima viva: ogni colore sul pezzo che governa davvero
          (nome+sottotitolo = Testo, ★ = Accento, bottone = Bottoni e titoli) */}
      <div className="brand-preview">
        <div className="brand-bar" style={{ background: colors.primary }} />
        <div className="brand-body" style={{ background: colors.background }}>
          <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 26, color: colors.text }}>
            {tenant.name}
          </div>
          <div style={{ fontSize: 12, color: colors.text, opacity: 0.7, marginTop: 4 }}>{tenant.sub}</div>
          <div style={{ marginTop: 10, color: colors.accent, fontSize: 15, letterSpacing: 2 }}>★★★★★</div>
          <div style={{ marginTop: 12 }}>
            <span
              style={{
                display: 'inline-block',
                padding: '8px 16px',
                borderRadius: 4,
                background: colors.primary,
                color: onPrimary,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {it ? 'Prenota un tavolo' : 'Book a table'}
            </span>
          </div>
        </div>
      </div>

      {/* i 4 campi colore, con la spiegazione di cosa governa ciascuno */}
      <div style={{ display: 'grid', gap: 14, marginTop: 18, maxWidth: 560 }}>
        {FIELDS.map((f) => (
          <div key={f.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <input
              type="color"
              value={colors[f.key]}
              onChange={(e) => setColors((c) => ({ ...c, [f.key]: e.target.value }))}
              style={{ width: 44, height: 34, border: '1px solid var(--line)', borderRadius: 6, padding: 2, background: 'none', cursor: 'pointer', flex: 'none' }}
              aria-label={it ? f.it : f.en}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{it ? f.it : f.en}</span>
                <input
                  type="text"
                  value={colors[f.key]}
                  onChange={(e) => {
                    const v = e.target.value.trim();
                    setColors((c) => ({ ...c, [f.key]: v }));
                  }}
                  onBlur={(e) => {
                    if (!HEX_RE.test(e.target.value.trim()))
                      setColors((c) => ({ ...c, [f.key]: defaults[f.key] }));
                  }}
                  style={{ width: 96, fontSize: 13, fontFamily: 'ui-monospace, monospace', padding: '5px 8px', border: '1px solid var(--line)', borderRadius: 6, background: 'var(--paper, #fff)' }}
                />
              </div>
              <div style={{ fontSize: 12, color: 'var(--mute)', marginTop: 3, lineHeight: 1.45 }}>
                {it ? f.descIt : f.descEn}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 20 }}>
        <button
          type="button"
          className="btn btn-primary"
          disabled={status === 'saving' || status === 'loading' || !Object.values(colors).every((v) => HEX_RE.test(v))}
          onClick={save}
        >
          {status === 'saving'
            ? it ? 'Pubblico…' : 'Publishing…'
            : it ? 'Salva e pubblica' : 'Save & publish'}
        </button>
        {status === 'done' && (
          <span style={{ fontSize: 13, color: 'var(--ok, #2e7d4f)' }}>
            {it ? 'Pubblicato ✓ — il sito si aggiorna in 2-3 minuti' : 'Published ✓ — live in 2-3 minutes'}
          </span>
        )}
        {status === 'error' && (
          <span style={{ fontSize: 13, color: '#b3543f' }}>
            {it
              ? 'Non sono riuscito a salvare i colori. Controlla la connessione e riprova.'
              : 'Could not save the colours. Check your connection and try again.'}
          </span>
        )}
      </div>
      <div className="sub" style={{ marginTop: 10 }}>
        {it
          ? 'L’Accento ridipinge anche questo portale al prossimo accesso.'
          : 'The Accent also repaints this portal on your next visit.'}
      </div>
    </div>
  );
}
